/**
 * Types for the CSV importers on the faux nokker list pages. One config per resource describes
 * its columns; the dialog, grid, validation and sample file are shared.
 */

/** One row as the grid holds it: column key to cell text. Every cell stays a string. */
export type ImportValues = Record<string, string>;

export interface ImportRow {
    /** Stable for the life of the dialog; the grid's row id. Not sent to HQ-API. */
    rowId: number;
    values: ImportValues;
}

export interface ImportOption {
    value: string;
    label: string;
    /** The record's own name, for a column that takes names as well as ids (`acceptsNames`). */
    name?: string;
}

/** Choice lists loaded from HQ-API when the dialog opens, keyed by `ImportLookupSource.key`. */
export type ImportLookups = Record<string, ImportOption[]>;

export interface ImportLookupSource {
    key: string;
    resource: string;
    filter?: Record<string, unknown>;
    label: (record: { id: number | string, name?: string }) => string;
}

export type ImportColumnKind =
    /** One line of text. */
    | 'text'
    /** Text that may run to several lines; edited in the row dialog, not in the cell. */
    | 'longText'
    /** One of `choices`. */
    | 'choice'
    /** Yes or no; true, 1, false and 0 are read as the same. */
    | 'boolean'
    /** One record id from the lookup named `lookup`. */
    | 'reference'
    /** Several record ids from the lookup named `lookup`, separated by semicolons. */
    | 'references';

export interface ImportColumn {
    /** The CSV header and the key HQ-API reads. */
    key: string;
    label: string;
    kind: ImportColumnKind;
    /** True, or a rule that reads the rest of the row (a company website needs its url). */
    required?: boolean | ((values: ImportValues) => boolean);
    maxLength?: number;
    /** A further check on a filled cell: the problem, or null when it passes. */
    check?: (value: string) => string | null;
    choices?: ImportOption[];
    lookup?: string;
    /**
     * A `references` column whose cells may name records as well as give their ids. Names are
     * turned into ids when the file loads; HQ-API accepts either.
     */
    acceptsNames?: boolean;
    /** Shown under the field in the row dialog. */
    helper?: string;
    /** Grid column width in px. */
    width?: number;
}

export interface CsvImportConfig {
    /** Plural name, as the list page calls it: "Nok Websites". */
    title: string;
    /** HQ-API route, relative to `hostUrl`. */
    endpoint: string;
    /** Base name for downloaded files. */
    fileName: string;
    columns: ImportColumn[];
    lookups: ImportLookupSource[];
    /** Rows for the sample file, built from live lookups so their ids exist. */
    sampleRows: (lookups: ImportLookups) => ImportValues[];
}

/**
 * What an import does with a row that matches a stored record (`AbstractNokImporter::MODES`):
 * `create` refuses it, `upsert` updates it and adds the rest, `update` refuses rows that match
 * nothing.
 */
export type ImportMode = 'create' | 'upsert' | 'update';

export const IMPORT_MODES: { value: ImportMode, label: string }[] = [
    { value: 'create', label: 'Add new only' },
    { value: 'upsert', label: 'Add and update' },
    { value: 'update', label: 'Update only' },
];

/** What HQ-API will do, or did, with one row. */
export type ImportAction = 'create' | 'update' | 'unchanged';

/** HQ-API's answer, from `AbstractNokImporter::import`. */
export interface ImportResult {
    status: 'valid' | 'invalid' | 'imported' | 'error';
    message: string;
    rowCount?: number;
    created?: number;
    updated?: number;
    unchanged?: number;
    /** Per row, by position in the batch sent; filled when every row passed. */
    actions?: { row: number, action: ImportAction }[];
    errors?: { row: number, field: string | null, message: string }[];
}

/** HQ-API's "Export for import": every record as import rows, `id` first. */
export interface ImportExport {
    status: 'ok' | 'error';
    message?: string;
    columns?: string[];
    rows?: ImportValues[];
}

/** Problems by row id, then by column key. `ROW_PROBLEM` holds a problem with the row as a whole. */
export type ImportProblems = Record<number, Record<string, string>>;

export const ROW_PROBLEM: string = '_row';

export const YES_NO: ImportOption[] = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];

/** The choices a column offers: its own, or yes and no for a boolean. */
export function choicesOf(column: ImportColumn): ImportOption[] | undefined {
    return column.kind === 'boolean' ? YES_NO : column.choices;
}
